// components/NavBar.js

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CampaignIcon from '@mui/icons-material/Campaign';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import MenuIcon from '@mui/icons-material/Menu';
import { useMediaQuery, useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useSettings } from 'src/@core/hooks/useSettings';
import ModeToggler from 'src/@core/layouts/components/shared-components/ModeToggler';
import { useAuth } from 'src/hooks/useAuth';
import ListItemButton from "@mui/material/ListItemButton";

const NavBar = ({ themeColor = '#7367F0' }) => {
  const { logout } = useAuth();
  const router = useRouter();
  const theme = useTheme();
  const { settings, saveSettings } = useSettings();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { campaign, team } = router.query;
  const [storedToken, setStoredToken] = useState();
  const [isNavigating, setIsNavigating] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    setStoredToken(window.localStorage.getItem('accessToken'));
  }, []);

  useEffect(() => {
    // Reset navigation state when route changes complete
    const handleRouteChangeComplete = () => {
      setIsNavigating(false);
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeComplete);
    };
  }, [router]);

  const handleNavigation = (path) => {
    setIsNavigating(true);
    router.push(path);
    setDrawerOpen(false);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleBackButton = () => {
    router.push(`/${campaign}`);
  };

  const showBackButton = campaign && team;

  const drawerList = () => (
    <Box
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
      role="presentation"
      sx={{ width: 250 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <ModeToggler saveSettings={saveSettings} settings={settings} />
      </Box>
      <Divider />
      <List>
        <ListItemButton disabled={isNavigating} onClick={() => handleNavigation('/')}>
          <HomeIcon />
          &nbsp;
          <ListItemText primary={isNavigating ? 'Loading...' : 'Home'} />
        </ListItemButton>
        <ListItemButton disabled={isNavigating} onClick={() => handleNavigation('/login')}>
          <LoginIcon />
          &nbsp;
          <ListItemText primary={isNavigating ? 'Loading...' : 'Login'} />
        </ListItemButton>
        <ListItemButton disabled={isNavigating} onClick={() => handleNavigation('/allcampaigns')}>
          <CampaignIcon />
          &nbsp;
          <ListItemText
            primary={isNavigating ? 'Loading...' : 'Live Campaigns'}
          />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <AppBar position="static" sx={{ background: themeColor }}>
      <Toolbar>
        {showBackButton && (
          <IconButton
            aria-label="back"
            color="inherit"
            onClick={handleBackButton}
            sx={{ mr: 2 }}
            size="large">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 32,
                height: 32,
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
            </Box>
          </IconButton>
        )}
        {isMobile && !showBackButton && (
          <IconButton
            aria-label="menu"
            color="inherit"
            onClick={toggleDrawer(true)}
            sx={{ mr: 2 }}
            size="large">
            <MenuIcon />
          </IconButton>
        )}
        <Typography
          component="div"
          sx={{
            flexGrow: 1,
            color: 'white',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 'bold',
            letterSpacing: '0.1em',
          }}
          variant="h4"
        >
          <Link href={'/'}>GivingAmp</Link>
        </Typography>
        {/* Landing Page */}
        {!isMobile && (
          <>
            {!campaign && (
              <>
                {/* <Button color="inherit" onClick={() => handleNavigation('/')}>Home</Button>
              <Button color="inherit" onClick={() => handleNavigation('/about')}>About</Button>
              <Button color="inherit" onClick={() => handleNavigation('/contact')}>Contact</Button> */}
              </>
            )}
            <Button
              color="inherit"
              disabled={isNavigating}
              onClick={() => handleNavigation('/allcampaigns')}
            >
              {isNavigating
                ? <CircularProgress color="inherit" size={20} />
                : 'Live Campaigns'}
            </Button>
            <ModeToggler saveSettings={saveSettings} settings={settings} />
            {storedToken
              ? <Button
                  color="inherit"
                  disabled={isLoggingOut}
                  onClick={async () => {
                    setIsLoggingOut(true);
                    await logout();
                    setIsLoggingOut(false);
                  }}
                >
                  {isLoggingOut
                    ? <CircularProgress color="inherit" size={20} />
                    : 'Logout'}
                </Button>
              : <Button
                  color="inherit"
                  disabled={isNavigating}
                  onClick={() => handleNavigation('/login')}
                >
                  {isNavigating
                    ? <CircularProgress color="inherit" size={20} />
                    : 'Login'}
                </Button>}
          </>
        )}
      </Toolbar>
      <Drawer anchor="left" onClose={toggleDrawer(false)} open={drawerOpen}>
        {drawerList()}
      </Drawer>
    </AppBar>
  );
};

export default NavBar;
